import { startCase, uniq } from 'es-toolkit';
import React from 'react';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, FormControl, FormHelperText, Paper, Radio } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { ActiveSnowLoad, LoadSettings, ThermalCondition } from '@paragon/contract-library';
import { angleLessThanOrEqual, stressToleranceInPoundsPerSquareFoot } from '@paragon/geometry-library';
import { hasFreeSubscription, UpgradeNotification } from '@paragon/login-tools';
import { NumericField } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { showUpgradeAccountDialog } from '../../../../../../store/actions/popupActions';
import { getSelectedModelForSettingsPanel } from '../../../../../../store/selectors/settingsPanelSelectors';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { MultiValueBooleanInput } from '../../multi-value-controls/MultiValueBooleanInput';
import { MultiValueFeetDistanceInput } from '../../multi-value-controls/MultiValueFeetDistanceInput';
import { MultiValueNumberInput } from '../../multi-value-controls/MultiValueNumberInput';
import { MultiValueSelectInput } from '../../multi-value-controls/MultiValueSelectInput';
import {
  Asce7Version,
  asceSection,
  asceSectionWithVersion,
  getAsce7Version,
  isAsce7_16OrLater,
  isAsce7_22OrLater,
} from '../loadingStandards';
import { LoadSettingsControlProps } from '../LoadSettingsControls';
import { DurationFactorSetting } from './DurationFactorSettings';
import { emphasizedControl } from './emphasizedControl';
import { getTerrainExposureCategoryName } from './getTerrainExposureCategoryName';
import { exposureCategories, terrainCategories, thermalConditions } from './loadSettingsEnumOptions';
import {
  calculateRoofSnowLoad,
  convertSnowLoadFromSettings,
  determineGroundSnowLoadParameter,
  getMinimumTopChordPitch,
  getThermalFactor,
  getThermalFactorOrExtractor,
} from './roofSnowLoadCalculation';

const nonBreakingSpace = '\u00A0';
const degreeSymbol = '\u00B0';
const dotOperator = '\u22C5';
const superscriptTwo = '\u00B2';

export function SnowLoadSettings({ disabled, loadSettingsArray, setLoadSettingsValues }: LoadSettingsControlProps) {
  const dispatch = useAppDispatch();

  const selectedModel = useAppSelector(getSelectedModelForSettingsPanel);

  // snow loads are not available to free users
  const isFreeUser = useAppSelector(hasFreeSubscription);
  if (isFreeUser) disabled = true;

  const asce7Versions = uniq(loadSettingsArray.map((loadSettings) => getAsce7Version(loadSettings.buildingCode)));
  const groundSnowLoadParameters = new Set(
    asce7Versions.map((asce7Version) => determineGroundSnowLoadParameter(asce7Version)),
  );

  const SnowLoadField = ({ snowLoadToSet }: { snowLoadToSet: ActiveSnowLoad }) => {
    const consistent = loadSettingsArray.every((loadSettings) => loadSettings.activeSnowLoad === snowLoadToSet);
    const loadSettingsValues = loadSettingsArray.map((loadSettings) =>
      convertSnowLoadFromSettings(loadSettings, snowLoadToSet),
    );

    return (
      <MultiValueNumberInput
        sx={{ m: 1, width: '100%', ml: 2 }}
        values={loadSettingsValues}
        submit={
          consistent
            ? (snowLoad) => setLoadSettingsValues({ snowLoad })
            : () => {
                throw new Error('Not settable');
              }
        }
        label={
          snowLoadToSet === 'Roof' ? (
            <span>
              Flat Roof Snow Load (p<sub>f</sub>)
            </span>
          ) : snowLoadToSet === 'UltimateGround' && groundSnowLoadParameters.has('AsdGround') ? (
            // This implies that p_g refers to the ASD ground snow load for some design, so don't label this p_g
            <span>Ultimate Ground Snow Load</span>
          ) : snowLoadToSet === 'AsdGround' && groundSnowLoadParameters.has('UltimateGround') ? (
            <span>
              ASD Ground Snow Load (p<sub>g(asd)</sub>)
            </span>
          ) : (
            <span>
              Ground Snow Load (p<sub>g</sub>)
            </span>
          )
        }
        unit="psf"
        numberOfDecimals={1}
        min={stressToleranceInPoundsPerSquareFoot}
        disabled={!consistent || disabled}
      />
    );
  };

  const shouldRenderUltimateGroundOption = groundSnowLoadParameters.has('UltimateGround');
  const renderSnowLoadOption = (snowLoadParameter: ActiveSnowLoad) => (
    <Box sx={{ display: 'flex', flex: 1, justifyContent: 'left' }}>
      <Radio
        checked={loadSettingsArray.some(
          (loadSettings) =>
            loadSettings.activeSnowLoad === snowLoadParameter ||
            (loadSettings.activeSnowLoad === 'UltimateGround' &&
              !shouldRenderUltimateGroundOption &&
              snowLoadParameter === 'AsdGround'),
        )}
        onChange={(_, checked) => {
          if (!checked) return;

          setLoadSettingsValues({
            activeSnowLoad: snowLoadParameter,
            snowLoad: convertSnowLoadFromSettings(loadSettingsArray[0], snowLoadParameter),
          });
        }}
        disabled={disabled}
      />
      <SnowLoadField snowLoadToSet={snowLoadParameter} />
    </Box>
  );

  const loadSettingsWithOtherThermalCondition = loadSettingsArray.filter(
    (loadSettings) => loadSettings.thermalCondition === 'Other',
  );

  // The manual thermal factor override is only honored when the calculated thermal factor is derived (i.e. the
  // condition resolves to an extractor function rather than a fixed number). If that isn't true for every selected
  // model, the controls below would misrepresent or silently ignore the override for some of them, so we lock them down.
  const supportsManualThermalFactor = (loadSettings: LoadSettings) =>
    typeof getThermalFactorOrExtractor(loadSettings.thermalCondition, getAsce7Version(loadSettings.buildingCode)) ===
    'function';
  const allLoadSettingsSupportManualThermalFactor = loadSettingsArray.every(supportsManualThermalFactor);

  const loadSettingsSinceAsce7_22 = loadSettingsArray.filter((loadSettings) => {
    const asce7Version = getAsce7Version(loadSettings.buildingCode);
    return asce7Version != null && isAsce7_22OrLater(asce7Version);
  });

  return (
    <Box>
      {isFreeUser && (
        <UpgradeNotification
          sx={{ marginBottom: 1 }}
          briefDescription="snow loads"
          onUpgradeClick={() => dispatch(showUpgradeAccountDialog())}
        />
      )}

      <Box>
        <FormControl
          fullWidth
          sx={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}
          disabled={disabled}
        >
          <FormHelperText sx={{ marginRight: '0px' }}>
            Note: Loads are generated without knowledge beyond the truss being analyzed.
          </FormHelperText>
          <TooltipIconButton
            title="The automatically-generated snow loads for a truss only take into account that truss&rsquo;s profile
            and these load settings. The shape and size of the rest of the roof and the presence of other nearby
            buildings may need to be accounted for using manual loads."
            disabled={disabled}
          >
            <HelpIcon />
          </TooltipIconButton>
        </FormControl>
      </Box>
      {shouldRenderUltimateGroundOption && renderSnowLoadOption('UltimateGround')}
      {renderSnowLoadOption('AsdGround')}
      {renderSnowLoadOption('Roof')}
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between' }}>
        {selectedModel && selectedModel.type !== 'layout-model' && loadSettingsArray.length === 1 ? (
          <NumericField
            value={calculateRoofSnowLoad(loadSettingsArray[0], selectedModel.model)}
            onSubmit={() => {
              throw new Error('Not settable');
            }}
            label={
              <span>
                Max Roof Snow Load p
                <sub>{angleLessThanOrEqual(getMinimumTopChordPitch(selectedModel.model), 5) ? 'f' : 's'}</sub>
              </span>
            }
            endAdornment="psf"
            numberOfDecimals={1}
            disabled
          />
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <MultiValueFeetDistanceInput
            sx={{ flex: 1 }}
            values={loadSettingsArray.map((loadSettings) => loadSettings.leftEaveToRidgeDistance)}
            submit={(leftEaveToRidgeDistance) => setLoadSettingsValues({ leftEaveToRidgeDistance })}
            label="Horizontal Eave to Ridge Distance (W)"
            min={0}
            disabled={disabled}
          />
          <TooltipIconButton
            title={<span>Horizontal Distance from Eave to Ridge. Consult {asceSection('7.6.1')} for diagrams.</span>}
          >
            <HelpIcon />
          </TooltipIconButton>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <MultiValueFeetDistanceInput
            sx={{ flex: 1 }}
            values={loadSettingsArray.map((loadSettings) => loadSettings.snowFetchDistancePerpendicularToTruss)}
            submit={(snowFetchDistancePerpendicularToTruss) =>
              setLoadSettingsValues({ snowFetchDistancePerpendicularToTruss })
            }
            label={
              <span>
                Length of roof upwind of the drift (L<sub>u</sub>)
              </span>
            }
            disabled={disabled}
          />
          <TooltipIconButton
            // prettier-ignore
            title={
            <span>
              Max out-of-plane distance to edge of roof.
              Used for determining the size and shape of snow drifts due to perpendicular-to-truss wind
              (see SBCA Load Guide Section C7.10.3 Note{nonBreakingSpace}4).
              Consult {asceSection('7.7.1')} for diagrams of l<sub>u</sub> for generic snow drifts.
            </span>
          }
          >
            <HelpIcon />
          </TooltipIconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <MultiValueSelectInput
            sx={{ flex: 1 }}
            property="Terrain Category"
            options={terrainCategories}
            values={loadSettingsArray.map((loadSettings) => loadSettings.terrainExposureCategory)}
            submit={(terrainExposureCategory) => setLoadSettingsValues({ terrainExposureCategory })}
            getName={getTerrainExposureCategoryName}
            disabled={disabled}
          />
          <TooltipIconButton
            // prettier-ignore
            title={
            <span>
              Used to determine the exposure factor, C<sub>e</sub>.
              Consult {asceSection('26.7.2')} on &ldquo;Surface roughness categories.&rdquo;
            </span>}
          >
            <HelpIcon />
          </TooltipIconButton>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <MultiValueSelectInput
            sx={{ flex: 1 }}
            property="Exposure Category"
            options={exposureCategories}
            values={loadSettingsArray.map((loadSettings) => loadSettings.exposure)}
            submit={(exposure) => setLoadSettingsValues({ exposure })}
            getName={startCase}
            disabled={disabled}
          />

          <TooltipIconButton
            // prettier-ignore
            title={
            <span>
              Used to determine the exposure factor, C<sub>e</sub>.
              Consult {asceSection('7.3.1')} for roof exposure definitions.
            </span>
          }
          >
            <HelpIcon />
          </TooltipIconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <MultiValueSelectInput
            sx={{ flex: 1 }}
            property={<span>Risk Category</span>}
            options={['I', 'II', 'III', 'IV'] as const}
            values={loadSettingsArray.map((loadSettings) => loadSettings.riskCategory)}
            submit={(riskCategory) => setLoadSettingsValues({ riskCategory })}
            disabled={disabled}
          />
          <TooltipIconButton
            // prettier-ignore
            title={
            <span>
              Classifies buildings based on consequences of building failure.
              Consult the governing building code (cf. IBC Section 1604.5).
            </span>
          }
          >
            <HelpIcon />
          </TooltipIconButton>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <MultiValueSelectInput
            sx={{ flex: 1 }}
            property={
              <span>
                Thermal Condition (C<sub>t</sub>)
              </span>
            }
            options={thermalConditions}
            values={loadSettingsArray.map((loadSettings) => loadSettings.thermalCondition)}
            submit={(thermalCondition) => setLoadSettingsValues({ thermalCondition })}
            getName={(value) => {
              const thermalFactors = uniq(
                asce7Versions.map((asce7Version) => getThermalFactorOrExtractor(value, asce7Version)),
              );
              return (
                <span>
                  {getThermalConditionDisplayName(asce7Versions, value)}
                  {thermalFactors.length !== 1 || typeof thermalFactors[0] === 'function'
                    ? null
                    : typeof thermalFactors[0] === 'number'
                      ? ` (${thermalFactors[0]})`
                      : assertExhaustiveSwitch(thermalFactors[0])}
                </span>
              );
            }}
            disabled={disabled}
          />
          <TooltipIconButton
            title={
              <span>Accounts for interior temperature of building. Consult {asceSection('7.3.2')} for details.</span>
            }
          >
            <HelpIcon />
          </TooltipIconButton>
        </Box>
      </Box>

      {loadSettingsWithOtherThermalCondition.length > 0 && (
        <Paper sx={[emphasizedControl, { p: 1, flexDirection: 'column' }]}>
          <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <MultiValueNumberInput
                sx={{ flex: 1 }}
                label="Roof R-Value"
                values={loadSettingsWithOtherThermalCondition.map((loadSettings) => loadSettings.rValue)}
                submit={(rValue) => setLoadSettingsValues({ rValue })}
                unit={`h${dotOperator}ft${superscriptTwo}${dotOperator}${degreeSymbol}F/Btu`}
                disabled={disabled}
                min={0}
              />
              <TooltipIconButton
                title={
                  <span>
                    Roof insulation R-value. This can affect sloped roof snow loads and overhang ice loads. See{' '}
                    {asceSection('7.4')}.
                  </span>
                }
              >
                <HelpIcon />
              </TooltipIconButton>
            </Box>

            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <MultiValueBooleanInput
                sx={{ m: 0 }}
                values={loadSettingsWithOtherThermalCondition.map((loadSettings) => loadSettings.isVentilated)}
                onChange={(isVentilated) => setLoadSettingsValues({ isVentilated })}
                label="Ventilated"
                disabled={disabled}
              />
              <TooltipIconButton
                title={
                  <span>
                    Roof ventilation can affect sloped roof snow loads and overhang ice loads. See {asceSection('7.4')}.
                  </span>
                }
              >
                <HelpIcon />
              </TooltipIconButton>
            </Box>
          </Box>

          {loadSettingsArray.some(supportsManualThermalFactor) && (
            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <MultiValueNumberInput
                  sx={{ flex: 1 }}
                  label={
                    <>
                      Thermal Factor (C<sub>t</sub>)
                    </>
                  }
                  values={loadSettingsArray.map(getThermalFactor)}
                  numberOfDecimals={2}
                  submit={(manualThermalFactor) => setLoadSettingsValues({ manualThermalFactor })}
                  disabled={
                    disabled ||
                    !allLoadSettingsSupportManualThermalFactor ||
                    loadSettingsArray.some((loadSettings) => loadSettings.manualThermalFactor == null)
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <MultiValueBooleanInput
                  sx={{ m: 0 }}
                  values={loadSettingsArray.map((loadSettings) => loadSettings.manualThermalFactor != null)}
                  onChange={(checked) =>
                    setLoadSettingsValues({
                      manualThermalFactor: checked ? getThermalFactor(loadSettingsArray[0]) : null,
                    })
                  }
                  label="Manual Thermal Factor"
                  disabled={disabled || !allLoadSettingsSupportManualThermalFactor}
                />
                <TooltipIconButton
                  title={
                    <span>
                      Override the calculated thermal factor with a value entered directly. Otherwise C<sub>t</sub> is
                      derived from the R-value.
                    </span>
                  }
                >
                  <HelpIcon />
                </TooltipIconButton>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        {loadSettingsSinceAsce7_22.length > 0 && (
          <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <MultiValueNumberInput
              sx={{ flex: 1 }}
              label={
                <span>
                  Winter Wind Parameter (W<sub>2</sub>)
                </span>
              }
              values={loadSettingsSinceAsce7_22.map((loadSettings) => loadSettings.winterWindParameter)}
              submit={(winterWindParameter) => setLoadSettingsValues({ winterWindParameter })}
              disabled={disabled}
              min={0}
              max={1}
            />
            <TooltipIconButton
              title={
                // The standard calls W_2 a percent. Comparing with real-world data shows that it is expressed per 1,
                // not per 100.
                <span>
                  Value between 0 and 1 representing the fraction of the time between October and April that the wind
                  speed exceeds 10 mph. This affects drift heights. See W<sub>2</sub> in{' '}
                  {asceSectionWithVersion(getAsce7Version(loadSettingsSinceAsce7_22[0].buildingCode), '7.1.2', '7.6.1')}
                  .
                </span>
              }
            >
              <HelpIcon />
            </TooltipIconButton>
          </Box>
        )}
        <DurationFactorSetting
          sx={{ flex: 1 }}
          loadType="snow"
          loadSettingsArray={loadSettingsArray}
          setLoadSettingsValues={setLoadSettingsValues}
          disabled={disabled}
        />
      </Box>

      <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
        <MultiValueBooleanInput
          sx={{ m: 0 }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.slipperyRoof)}
          onChange={(slipperyRoof) => setLoadSettingsValues({ slipperyRoof })}
          label="Slippery roof"
          disabled={disabled}
        />
        <TooltipIconButton
          // prettier-ignore
          title={
            <span>
              Allows reduction of snow load (p<sub>s</sub>) if the roof is slippery and unobstructed.
              This requires sufficient space below the eaves to accept sliding snow.
              Slippery surfaces include metal, slate, glass, and bituminous, rubber, and plastic membranes with a smooth surface.
              Asphalt shingles, wood shingles, and shakes are not considered slippery.
              Consult {asceSection('7.4')} for details.
            </span>}
        >
          <HelpIcon />
        </TooltipIconButton>
      </Box>
      <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
        <MultiValueBooleanInput
          sx={{ m: 0 }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.isSawtoothRoof)}
          onChange={(isSawtoothRoof) => setLoadSettingsValues({ isSawtoothRoof })}
          label="Sawtooth roof"
          disabled={disabled}
        />
        <TooltipIconButton
          // prettier-ignore
          title={
            <span>
              Enable to produce unbalanced snow load cases based on the roof shape as specified by {asceSection("7.6.3")}.
            </span>}
        >
          <HelpIcon />
        </TooltipIconButton>
      </Box>
      <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
        <MultiValueBooleanInput
          sx={{ m: 0 }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.includeDriftLoads)}
          onChange={(includeDriftLoads) => setLoadSettingsValues({ includeDriftLoads })}
          label="Consider drifting snow (step drifts, parapets)"
          disabled={disabled}
        />
        <TooltipIconButton
          // prettier-ignore
          title={
            <span>
              Enable to produce drifting snow loads based on the roof shape as specified by {asceSection("7.7", "7.8")}.
            </span>}
        >
          <HelpIcon />
        </TooltipIconButton>
      </Box>
    </Box>
  );
}

function getThermalConditionDisplayName(
  asce7Versions: readonly (Asce7Version | null)[],
  thermalCondition: ThermalCondition,
): string {
  const allAsceVersionsPrecede7_16 = asce7Versions.every(
    (asce7Version) => asce7Version != null && !isAsce7_16OrLater(asce7Version),
  );

  return allAsceVersionsPrecede7_16 && thermalCondition === 'FreezerBuilding'
    ? 'Below Freezing'
    : startCase(thermalCondition);
}
